/**
 * Display-currency context (#43, 2026-06-12).
 *
 * Holds the user's chosen DISPLAY currency (ZAR authoritative default, or the
 * USD indicative approximation) and exposes a bound formatter. Storage stays
 * ZAR-cents everywhere — this only changes how an amount is rendered.
 *
 * Split context/hook/provider as elsewhere (auth, i18n) for fast-refresh.
 */

import { createContext } from 'react';
import {
  DEFAULT_DISPLAY_CURRENCY,
  formatDisplayAmount,
  type DisplayCurrency,
} from './display';

export interface DisplayCurrencyContextValue {
  /** Active DISPLAY currency. ZAR is the authoritative default. */
  display: DisplayCurrency;
  /** Switch the display currency (persisted to localStorage by the provider). */
  setDisplay: (c: DisplayCurrency) => void;
  /**
   * Format ZAR cents in the active display currency. Convenience over calling
   * `formatDisplayAmount(cents, display)` directly so consumers don't have to
   * thread `display` through.
   */
  format: (zarCents: number) => string;
  /** True when the active display is the indicative (non-authoritative) USD. */
  isIndicative: boolean;
}

export const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue>({
  display: DEFAULT_DISPLAY_CURRENCY,
  setDisplay: () => {},
  format: (zarCents) => formatDisplayAmount(zarCents, DEFAULT_DISPLAY_CURRENCY),
  isIndicative: false,
});
